/**
 * MoneyBook ↔ workflow (`?entry=books`): secure postMessage bridge.
 *
 * - MONEYBOOK_SET_TOKEN: opener or parent iframe sends Firebase custom token (existing).
 * - WORKFLOW_READY: workflow notifies MoneyBook it can send the token.
 * - MONEYBOOK_TOGGLE_API_ENV: flip prod/local API (same as ClinicAppBar / UserContext toggle).
 * - MONEYBOOK_SET_API_ENV: `{ useProd: boolean }` to set explicitly.
 *
 * Parent menu (e.g. moneybook.ai profile dropdown) should only expose these to trusted admins.
 * Implementation: `iframe.contentWindow.postMessage({ type: MONEYBOOK_TOGGLE_API_ENV }, workflowOrigin)`
 * or `{ type: MONEYBOOK_SET_API_ENV, useProd: true }`.
 *
 * Allowed origins: moneybook.ai, localhost dev ports.
 */

import { useEffect, useRef } from 'react';
import { getBooksEntry } from '../lib/booksEntry';
import { useUser } from './UserContext';

const ALLOWED_OPENER_ORIGINS = [
    'https://moneybook.ai',
    'https://www.moneybook.ai',
    'http://localhost:3000',
    'http://localhost:3001',
    'https://localhost:3000',
    'https://localhost:3001',
];

const MONEYBOOK_SET_TOKEN = 'MONEYBOOK_SET_TOKEN';
const WORKFLOW_READY = 'WORKFLOW_READY';
const MONEYBOOK_TOGGLE_API_ENV = 'MONEYBOOK_TOGGLE_API_ENV';
const MONEYBOOK_SET_API_ENV = 'MONEYBOOK_SET_API_ENV';

export function MoneyBookPostMessageHandler() {
    const { setTokenFromMoneyBook, toggleApiEnv, setApiEnvUseProd } = useUser();
    const readySent = useRef(false);

    useEffect(() => {
        const entry = getBooksEntry();
        if (entry !== 'books') return;

        const handleMessage = (event: MessageEvent) => {
            if (!ALLOWED_OPENER_ORIGINS.includes(event.origin)) return;

            if (event.data?.type === MONEYBOOK_SET_TOKEN && typeof event.data?.token === 'string') {
                setTokenFromMoneyBook(event.data.token);
                return;
            }

            if (event.data?.type === MONEYBOOK_TOGGLE_API_ENV) {
                toggleApiEnv();
                return;
            }

            if (
                event.data?.type === MONEYBOOK_SET_API_ENV &&
                typeof event.data?.useProd === 'boolean'
            ) {
                setApiEnvUseProd(event.data.useProd);
            }
        };

        window.addEventListener('message', handleMessage);

        const sendReady = () => {
            if (readySent.current) return;
            try {
                const payload = { type: WORKFLOW_READY, entry: 'books' as const };
                if (window.opener && !window.opener.closed) {
                    window.opener.postMessage(payload, '*');
                }
                if (window.parent !== window) {
                    window.parent.postMessage(payload, '*');
                }
                readySent.current = true;
            } catch {
                // cross-origin or closed
            }
        };

        requestAnimationFrame(() => {
            sendReady();
        });
        const t = window.setTimeout(sendReady, 500);
        return () => {
            window.clearTimeout(t);
            window.removeEventListener('message', handleMessage);
        };
    }, [setTokenFromMoneyBook, toggleApiEnv, setApiEnvUseProd]);

    return null;
}

export {
    WORKFLOW_READY,
    MONEYBOOK_SET_TOKEN,
    MONEYBOOK_TOGGLE_API_ENV,
    MONEYBOOK_SET_API_ENV,
};
